import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  Switch,
  FormControlLabel,
  LinearProgress,
  Collapse,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Paper,
  Select,
  InputLabel,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { carrierExportHeaders, carrierTemplateSampleRows, downloadCarrierTemplateCsv } from '../../utils/carrierExport';
import RichTextEditor from './RichTextEditor';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  Code,
  Link as LinkIcon,
  FormatListBulleted,
  FormatListNumbered,
  FormatClear,
  Add,
  Send,
  Delete,
  AttachFile,
  FormatQuote,
  Title,
  History,
  Schedule,
  Preview,
  Analytics,
  CheckCircle,
  Warning,
  Info,
  ExpandMore,
  ExpandLess,
  ContentCopy,
  Settings,
  Palette,
  AccessTime,
  TrendingUp,
  Download,
  GpsFixed,
  AutoAwesome,
  Shield,
  Lightbulb,
  Edit,
  Refresh,
} from '@mui/icons-material';
import { analyzeSpamScore, suggestReplacements } from './SpamDetector';
import { generateEmailWithAI, rewriteToAvoidSpam, EMAIL_TEMPLATES_SPAM_FREE, getAPIKeyInstructions } from './AIEmailWriter';
import AIEmailWriterCard from './AIEmailWriterCard';

const AdvancedEmailComposer = ({
  compose = {},
  setCompose,
  accounts = [],
  carrierEmails = [],
  onSend,
  sendLoading = false,
  recipientStats = {},
  subjectInsights = {},
  bodyInsights = {},
  fileInputRef,
  onFileImport,
  onLoadCarrierExport,
  uploadError,
  mergeTags = [],
  trackingProvider = 'none',
  trackingIntegrationConnected = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [editorMode, setEditorMode] = useState('visual');
  const [previewMode, setPreviewMode] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [priority, setPriority] = useState('normal');
  const [draftHistory, setDraftHistory] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [recipientsExpanded, setRecipientsExpanded] = useState(true);
  
  // Auto-save draft history and template columns
  const templateColumns = carrierExportHeaders;

  const downloadTemplate = () => {
    downloadCarrierTemplateCsv({ filename: 'email_recipients_template.csv', rows: carrierTemplateSampleRows });
  };

  const handleLoadCarrierExport = () => {
    if (onLoadCarrierExport) {
      onLoadCarrierExport();
    }
  };

  // Auto-save drafts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (compose.body || compose.subject) {
        const newDraft = {
          id: Date.now(),
          subject: compose.subject,
          body: compose.body.substring(0, 100),
          timestamp: new Date().toLocaleTimeString(),
        };
        setDraftHistory((prev) => [newDraft, ...prev].slice(0, 10));
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [compose.body, compose.subject]);

  const handleRecipientsFromManual = () => {
    const emails = compose.manualBlock
      .split(/[,\n\s]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));

    setCompose((prev) => ({
      ...prev,
      recipients: [...new Set([...(prev.recipients || []), ...emails])],
      manualBlock: '',
    }));
  };

  const handleAttachFile = (files) => {
    setAttachments((prev) => [...prev, ...files]);
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const copyDraft = (draft) => {
    setCompose((prev) => ({
      ...prev,
      subject: draft.subject,
      body: draft.body,
    }));
    setHistoryOpen(false);
  };

  const calculateSendTime = () => {
    if (scheduleDate && scheduleTime) {
      return `Scheduled for ${scheduleDate} at ${scheduleTime}`;
    }
    return 'Send immediately';
  };

  const getDeliverabilityScore = () => {
    let score = 50;
    if (compose.subject && compose.subject.length > 20 && compose.subject.length < 65) score += 15;
    if (compose.body && compose.body.length > 100) score += 15;
    if (bodyInsights?.hasGreeting) score += 10;
    if (!subjectInsights?.spamHits?.length) score += 10;
    return Math.min(score, 100);
  };

  // AI Email Generation Handler
  const handleGenerateAI = async () => {
    if (!aiPrompt.trim()) {
      alert('Please enter what you want the email to be about');
      return;
    }
    
    setAiLoading(true);
    try {
      const result = await generateEmailWithAI(aiPrompt, {
        tone: aiTone,
        length: aiLength,
        industry: 'logistics',
        includeGreeting: true,
        includeClosing: true,
        avoidSpam: true,
        mergeTags: mergeTags, // Pass system merge tags to AI
      });
      
      if (result.success) {
        // Extract subject from AI response if exists
        // Handle both plain text and HTML formatted responses
        const emailContent = result.email.trim();
        
        console.log('🔍 AI Response Preview (first 500 chars):', emailContent.substring(0, 500));
        console.log('📏 Total AI response length:', emailContent.length);
        
        // Try multiple patterns to find subject line
        let subjectMatch = null;
        let subject = '';
        let body = '';
        
        // Pattern 1: HTML format with <p><strong>Subject:...
        subjectMatch = emailContent.match(/<p><strong>Subject:\s*(.+?)<\/strong><\/p>/i);
        if (subjectMatch) {
          console.log('✅ Found subject using HTML <p><strong> pattern');
          subject = subjectMatch[1].replace(/<[^>]+>/g, '').trim();
          body = emailContent.replace(/<p><strong>Subject:\s*.+?<\/strong><\/p>/i, '').trim();
        }
        
        // Pattern 2: Plain text format "Subject: ..."
        if (!subject) {
          subjectMatch = emailContent.match(/^Subject:\s*(.+?)$/im);
          if (subjectMatch) {
            console.log('✅ Found subject using plain text pattern');
            subject = subjectMatch[1].trim();
            // Remove subject line and everything before first <p> or actual content
            body = emailContent.replace(/^Subject:\s*.+?(\n|$)/im, '').trim();
          }
        }
        
        // Pattern 3: Subject anywhere in first few lines
        if (!subject) {
          const lines = emailContent.split('\n');
          for (let i = 0; i < Math.min(5, lines.length); i++) {
            if (lines[i].toLowerCase().includes('subject:')) {
              subject = lines[i].replace(/subject:/i, '').replace(/<[^>]+>/g, '').trim();
              body = lines.slice(i + 1).join('\n').trim();
              console.log('✅ Found subject in line', i);
              break;
            }
          }
        }
        
        // Clean up body - remove empty paragraphs at start/end
        if (body) {
          body = body.replace(/^(<p>\s*<\/p>\s*)+/, '').replace(/(<p>\s*<\/p>\s*)+$/, '').trim();
        }
        
        if (subject && body) {
          console.log('✅ Extracted Subject:', subject.substring(0, 80));
          console.log('✅ Extracted Body Length:', body.length);
          console.log('✅ Body Preview (first 200 chars):', body.substring(0, 200));
          
          setCompose(prev => {
            console.log('🔄 Updating compose state with subject and body');
            return { ...prev, subject, body };
          });
        } else if (emailContent) {
          // No subject found, use entire content as body
          console.log('⚠️ No subject line found, using full content as body');
          console.log('📝 Setting body length:', emailContent.length);
          
          setCompose(prev => {
            console.log('🔄 Updating compose state with full content as body');
            return { ...prev, body: emailContent };
          });
        }
        
        // Small delay to ensure state update, then close dialog
        setTimeout(() => {
          console.log('✅ Closing AI dialog');
          setAiDialogOpen(false);
          setAiPrompt('');
        }, 100);
      } else {
        // More detailed error message
        const errorMsg = result.error || 'Unknown error';
        console.error('AI Generation Failed:', errorMsg);
        
        // Check if it's an API key issue
        if (errorMsg.toLowerCase().includes('api key') || errorMsg.toLowerCase().includes('no valid api key')) {
          alert(`❌ API Key Not Found\n\nThe AI Email Writer needs a Groq API key to work.\n\n📋 SETUP INSTRUCTIONS:\n\n1. Open .env file in project root\n2. Find line: REACT_APP_GROQ_API_KEY=gsk_...\n3. Make sure your API key is there\n4. IMPORTANT: Restart dev server:\n   - Stop current server (Ctrl+C)\n   - Run: npm start\n5. Or rebuild: npm run build\n\n💡 Environment variables load only at startup!\n\nCurrent Status: ${errorMsg}`);
        } else if (errorMsg.toLowerCase().includes('unauthorized') || errorMsg.toLowerCase().includes('401')) {
          alert(`❌ API Key Invalid\n\nThe Groq API key is invalid or expired.\n\n📋 FIX STEPS:\n\n1. Visit: https://console.groq.com/keys\n2. Create new API key (FREE)\n3. Update .env file:\n   REACT_APP_GROQ_API_KEY=your_new_key_here\n4. Restart server: npm start\n\nError: ${errorMsg}`);
        } else if (errorMsg.toLowerCase().includes('rate limit') || errorMsg.toLowerCase().includes('429')) {
          alert(`⏱️ Rate Limit Reached\n\nToo many requests. Please wait a moment and try again.\n\nError: ${errorMsg}`);
        } else if (errorMsg.toLowerCase().includes('400') || errorMsg.toLowerCase().includes('bad request')) {
          alert(`❌ Invalid Request\n\nThe request to Groq API was rejected.\n\nPossible causes:\n• API key format incorrect\n• Request too large\n• Invalid parameters\n\n📋 Quick Fix:\n1. Check .env file has valid key\n2. Restart dev server: npm start\n3. Try again with shorter prompt\n\nTechnical error: ${errorMsg}`);
        } else {
          alert(`❌ AI Generation Error\n\n${errorMsg}\n\nUsing fallback template instead.`);
          if (result.fallback) {
            setCompose(prev => ({ ...prev, body: result.fallback }));
          }
        }
      }
    } catch (error) {
      alert('AI generation failed: ' + error.message);
    } finally {
      setAiLoading(false);
    }
  };
  
  // Rewrite Email to Remove Spam Words
  const handleRewriteSpamFree = async () => {
    if (!compose.body) {
      alert('Write some email content first!');
      return;
    }
    
    setRewriteLoading(true);
    try {
      const result = await rewriteToAvoidSpam(compose.body, spamAnalysis?.highlightWords || []);
      
      if (result.success) {
        setCompose(prev => ({ ...prev, body: result.email }));
      } else {
        alert(`Rewrite failed: ${result.error}`);
      }
    } catch (error) {
      alert('Rewrite failed: ' + error.message);
    } finally {
      setRewriteLoading(false);
    }
  };
  
  // Use Pre-built Spam-Free Template
  const handleUseTemplate = (templateKey) => {
    const template = EMAIL_TEMPLATES_SPAM_FREE[templateKey];
    if (template) {
      setCompose(prev => ({
        ...prev,
        subject: template.subject,
        body: template.body,
      }));
    }
  };

  const score = getDeliverabilityScore();
  const usesInternalTracking = trackingProvider === 'internal';
  const usesExternalTracking = trackingProvider === 'mailsuite';
  const trackingEnabled = usesInternalTracking && (compose.trackOpens ?? true);
  const clickTrackingEnabled = compose.trackClicks ?? true;
  const readReceiptEnabled = compose.readReceipt ?? false;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2 }}>
      {/* Account & Subject Section */}
      <Grid container spacing={isMobile ? 1 : 2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Send from account"
            value={compose.accountId || ''}
            onChange={(e) => setCompose((prev) => ({ ...prev, accountId: e.target.value }))}
            size={isMobile ? 'small' : 'medium'}
            variant="outlined"
          >
            <MenuItem value="">Auto-select</MenuItem>
            {accounts.map((acc) => (
              <MenuItem key={acc.id} value={acc.id}>
                {acc.label || acc.fromEmail} • {acc.fromName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email Subject"
            value={compose.subject}
            onChange={(e) => setCompose((prev) => ({ ...prev, subject: e.target.value }))}
            size={isMobile ? 'small' : 'medium'}
            placeholder="Enter compelling subject line"
            helperText={`${compose.subject.length}/70 chars`}
            error={subjectInsights?.tooShort || subjectInsights?.tooLong}
          />
        </Grid>
      </Grid>

      {/* Subject Insights */}
      {compose.subject && (
        <Card variant="outlined" sx={{ background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)' }}>
          <CardContent sx={{ py: 1, px: isMobile ? 1.5 : 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
              {subjectInsights?.tooShort && (
                <Chip size="small" icon={<Warning />} label="Subject too short" color="warning" />
              )}
              {subjectInsights?.tooLong && (
                <Chip size="small" icon={<Warning />} label="Subject too long" color="warning" />
              )}
              {subjectInsights?.spamHits?.length > 0 && (
                <Chip
                  size="small"
                  icon={<Warning />}
                  label={`Spam triggers: ${subjectInsights.spamHits.slice(0, 2).join(', ')}`}
                  color="error"
                />
              )}
              {!subjectInsights?.tooShort && !subjectInsights?.tooLong && !subjectInsights?.spamHits?.length && (
                <Chip size="small" icon={<CheckCircle />} label="Subject looks great" color="success" />
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {usesExternalTracking && (
        <Alert severity={trackingIntegrationConnected ? 'info' : 'warning'}>
          {trackingIntegrationConnected
            ? 'MailSuite integration is selected. App-side legacy pixel tracking stays disabled.'
            : 'MailSuite is selected but not connected in admin portal. Emails will send without app-side open tracking.'}
        </Alert>
      )}

      {trackingProvider === 'none' && (
        <Alert severity="warning">
          Email tracking is disabled. Connect a provider from the admin portal if you want tracked sends.
        </Alert>
      )}

      {/* AI Email Writer & Spam Detection - Reusable Component */}
      <AIEmailWriterCard
        subject={compose.subject || ''}
        body={compose.body || ''}
        onUpdate={({ subject, body }) => {
          setCompose(prev => ({
            ...prev,
            ...(subject !== undefined && { subject }),
            ...(body !== undefined && { body })
          }));
        }}
        mergeTags={mergeTags}
        buttonText="Generate Email"
        isMobile={isMobile}
      />

      {/* Main Email Composition Card */}
      {/* Message Body with Formatting */}
      <Card
        variant="outlined"
        sx={{
          overflow: 'hidden',
          borderRadius: 2,
          borderColor: 'rgba(15,23,42,0.12)',
          background: 'linear-gradient(180deg, rgba(15,23,42,0.01) 0%, rgba(14,165,233,0.03) 100%)',
        }}
      >
        {/* Message Body */}
        <CardContent sx={{ p: isMobile ? 1 : 1.5 }}>
          {!previewMode ? (
            <RichTextEditor
              value={compose.body || ''}
              onChange={(html) => setCompose((prev) => ({ ...prev, body: html }))}
              placeholder="Compose your professional email here... Use formatting, images, and attachments"
              height={isMobile ? 300 : 400}
              minHeight={isMobile ? 200 : 250}
              onAttachFile={handleAttachFile}
              attachments={attachments}
              onRemoveAttachment={handleRemoveAttachment}
              mergeTags={mergeTags}
            />
          ) : (
            <Box
              sx={{
                p: 2,
                backgroundColor: '#f9f9f9',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                minHeight: isMobile ? 200 : 300,
                fontSize: '0.95rem',
                lineHeight: 1.6,
              }}
              dangerouslySetInnerHTML={{ __html: compose.body || '(No message body)' }}
            />
          )}
        </CardContent>
      </Card>

      {/* Message Insights */}
      {compose.body && (
        <Card variant="outlined" sx={{ background: 'linear-gradient(135deg, rgba(76,175,80,0.1) 0%, rgba(56,142,60,0.1) 100%)' }}>
          <CardContent sx={{ py: 1, px: isMobile ? 1.5 : 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
              <Stack>
                <Typography fontWeight={700} variant="body2">
                  Message Insights
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {bodyInsights?.wordCount || 0} words • {bodyInsights?.linkCount || 0} links • {compose.body.length} chars
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Chip
                  size="small"
                  icon={bodyInsights?.wordCount > 0 ? <CheckCircle /> : <Info />}
                  label={`${bodyInsights?.wordCount || 0} words`}
                  color={bodyInsights?.wordCount > 0 ? 'success' : 'default'}
                />
                <Chip size="small" label={`${bodyInsights?.linkCount || 0} links`} variant="outlined" />
                {bodyInsights?.hasGreeting && (
                  <Chip size="small" icon={<CheckCircle />} label="Greeting" color="success" />
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Recipients Section - Collapsible */}
      <Card variant="outlined" sx={{ border: '2px dashed #667eea' }}>
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={recipientsExpanded ? 1 : 0}
          >
            <Typography fontWeight={700}>Recipients ({Math.max(recipientStats?.total || 0, compose.recipients?.length || 0)})</Typography>
            <IconButton
              size="small"
              onClick={() => setRecipientsExpanded((prev) => !prev)}
            >
              {recipientsExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Stack>

          <Collapse in={recipientsExpanded}>
            <Stack spacing={2}>
              {/* Action Buttons */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={handleRecipientsFromManual}
                  disabled={!compose.manualBlock.trim()}
                  fullWidth={isMobile}
                >
                  Add from list
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={downloadTemplate}
                  fullWidth={isMobile}
                >
                  Download template
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AttachFile />}
                  onClick={() => fileInputRef?.current?.click()}
                  fullWidth={isMobile}
                >
                  Upload CSV/Excel
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<GpsFixed />}
                  onClick={handleLoadCarrierExport}
                  fullWidth={isMobile}
                >
                  Load from Carrier Search
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add />}
                  disabled={!carrierEmails.length}
                  onClick={() =>
                    setCompose((prev) => ({
                      ...prev,
                      recipients: [
                        ...(prev.recipients || []),
                        ...carrierEmails.filter((e) => !prev.recipients?.includes(e)),
                      ],
                    }))
                  }
                  fullWidth={isMobile}
                >
                  + {carrierEmails.length} Carriers
                </Button>
              </Stack>

              {/* Manual Input */}
              <TextField
                fullWidth
                multiline
                minRows={2}
                placeholder="Paste emails separated by comma or new line"
                value={compose.manualBlock}
                onChange={(e) => setCompose((prev) => ({ ...prev, manualBlock: e.target.value }))}
                size="small"
              />

              {uploadError && <Alert severity="warning">{uploadError}</Alert>}

              {/* Recipient Display */}
              {compose.recipients?.length > 0 && (
                <Box>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {compose.recipients.map((email) => (
                      <Chip
                        key={email}
                        label={email}
                        onDelete={() =>
                          setCompose((prev) => ({
                            ...prev,
                            recipients: prev.recipients.filter((r) => r !== email),
                          }))
                        }
                        size="small"
                        color="primary"
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Collapse>
        </CardContent>
      </Card>

      {/* Send Section */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button variant="outlined" startIcon={<Delete />} onClick={() => setCompose({ accountId: '', subject: '', body: '', recipients: [], manualBlock: '' })}>
          Clear
        </Button>
        <Button
          variant="outlined"
          startIcon={<Analytics />}
          onClick={() => {
            if (!usesInternalTracking) return;
            setCompose((prev) => ({ ...prev, trackOpens: !trackingEnabled }));
          }}
          disabled={!usesInternalTracking}
          sx={{ borderColor: trackingEnabled ? 'rgba(16,185,129,0.5)' : 'rgba(15,23,42,0.2)' }}
        >
          Tracking: {usesInternalTracking ? (trackingEnabled ? 'On' : 'Off') : trackingProvider === 'mailsuite' ? 'MailSuite' : 'Disabled'}
        </Button>
        <Button
          variant="contained"
          startIcon={<Send />}
          onClick={onSend}
          disabled={sendLoading || !compose.subject.trim() || !compose.body.trim() || !compose.recipients?.length}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            fontWeight: 700,
            minWidth: isMobile ? 'auto' : 150,
          }}
        >
          {sendLoading ? 'Sending...' : 'Send'}
        </Button>
      </Box>

      {/* Draft History Dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>📜 Draft History</DialogTitle>
        <DialogContent>
          {draftHistory.length === 0 ? (
            <Typography color="text.secondary">No drafts saved yet</Typography>
          ) : (
            <List>
              {draftHistory.map((draft) => (
                <ListItem
                  key={draft.id}
                  button
                  onClick={() => copyDraft(draft)}
                  sx={{ borderBottom: '1px solid #eee' }}
                >
                  <ListItemText
                    primary={draft.subject || '(No subject)'}
                    secondary={`${draft.body}... • ${draft.timestamp}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>🕐 Schedule Email</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} fullWidth />
            <TextField type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} fullWidth />
            <Alert severity="info">{calculateSendTime()}</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleOpen(false)}>Cancel</Button>
          <Button variant="contained">Schedule</Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Email Settings</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={trackingEnabled}
                  onChange={(e) => setCompose((prev) => ({ ...prev, trackOpens: e.target.checked }))}
                  disabled={!usesInternalTracking}
                />
              }
              label={usesInternalTracking ? 'Enable open tracking' : 'Open tracking managed by selected provider'}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={readReceiptEnabled}
                  onChange={(e) => setCompose((prev) => ({ ...prev, readReceipt: e.target.checked }))}
                />
              }
              label="Request read receipt"
            />
            <TextField select label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)} fullWidth>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={analyticsOpen} onClose={() => setAnalyticsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>📈 Email Analytics Preview</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2">Deliverability Score</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress variant="determinate" value={score} />
                </Box>
                <Typography fontWeight={700}>{score}%</Typography>
              </Stack>
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle2">📊 Expected Performance</Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Open Rate" secondary="~25-35% (industry avg)" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Click Rate" secondary="~2-5% (industry avg)" />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Reply Rate" secondary="~0.5-1% (industry avg)" />
                </ListItem>
              </List>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalyticsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: 'none' }}
        onChange={onFileImport}
      />
    </Box>
  );
};

export default AdvancedEmailComposer;

